import {
    forwardRef,
    InputHTMLAttributes,
    useState,
    useCallback,
    KeyboardEventHandler,
    useRef,
    SyntheticEvent,
    useEffect,
} from 'react';
import classnames from 'classnames';

import { findNextFocusableElement, keyboardKeys, keyboardMatch } from '@hh.ru/magritte-common-keyboard';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { CustomChip } from '@hh.ru/magritte-ui-chips';
import { ChipsTriggerProps } from '@hh.ru/magritte-ui-chips-trigger/types';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { ChevronDownOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { useFocus } from '@hh.ru/magritte-ui-input/useFocus';

import styles from './chips-trigger.less';

const focusOnLastChip = (event: SyntheticEvent, chipsContainer: HTMLElement | null, input: HTMLElement | null) => {
    if (!chipsContainer || !input) {
        return;
    }
    const nextFocusableElement = findNextFocusableElement(chipsContainer, input, 'left', 'list');
    if (nextFocusableElement !== null) {
        event.preventDefault();
        nextFocusableElement.focus();
    }
};

type State = {
    hadFocusWhenClicked: boolean;
    wasExpandedWhenClicked: boolean;
};

export const ChipsTrigger = forwardRef<
    HTMLInputElement,
    ChipsTriggerProps & Omit<InputHTMLAttributes<HTMLInputElement>, keyof ChipsTriggerProps>
>(
    (
        {
            size = 'large',
            placeholder,
            disabled,
            expanded: externalExpanded,
            toggleExpanded: toggleExternalExpanded,
            state,
            invalid,
            maxHeight,
            description,
            errorMessage,
            wrapperRef,
            onChange,
            onFocus,
            onBlur,
            onKeyDown,
            onChangeInput: onChangeInputExternal,
            allowArbitraryValues = true,
            'data-qa': dataQa = 'chips-trigger',
        },
        ref
    ) => {
        const { isMobile } = useBreakpoint();
        const [expanded, setExpanded] = useState(false);
        const [isFocusVisible, setFocusVisible] = useState(false);
        const stateRef = useRef<State>({ hadFocusWhenClicked: false, wasExpandedWhenClicked: false });

        const chipsRootRef = useRef<HTMLDivElement>(null);
        const chipsRootRefCallback = useMultipleRefs(wrapperRef, chipsRootRef);
        const chipsContainerRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        const inputRefCallback = useMultipleRefs(ref, inputRef);
        const showLabel = state.value === '' && state.chips.length === 0 && placeholder !== '';

        const onChangeInput = useCallback(
            (value: string) => {
                onChange({
                    value,
                    chips: state.chips,
                    event: { type: 'change', modifier: 'keyboard' },
                });
                onChangeInputExternal?.(value);
            },
            [state.chips, onChange, onChangeInputExternal]
        );
        const { changeHandler, focusHandler, blurHandler, isFocused } = useFocus(onChangeInput, onFocus, onBlur);

        useEffect(() => {
            if (inputRef.current) {
                setFocusVisible(isFocused && inputRef.current.classList.contains('focus-visible'));
            }
        }, [isFocused]);

        const deleteHandler = useCallback(
            (index: number) => {
                const deletedChip = state.chips.splice(index, 1);
                onChange({
                    value: state.value,
                    chips: [...state.chips],
                    event: { type: 'remove', modifier: 'cross', element: deletedChip?.[0] },
                });
                // не теряем фокус с инпута
                inputRef.current?.focus();
            },
            [onChange, state.chips, state.value]
        );

        const keyDownHandler = useCallback<KeyboardEventHandler>(
            (event) => {
                // создаем новый чипс и затираем текстовый инпут
                if (allowArbitraryValues && keyboardMatch(event.nativeEvent, keyboardKeys.Enter)) {
                    if (state.value.length === 0) {
                        return;
                    }
                    onChange({
                        value: '',
                        chips: [...state.chips, { value: state.value, userInput: true }],
                        event: { type: 'add', modifier: 'enter' },
                    });
                    onChangeInputExternal?.('');
                }
                // удаляем последний созданный чипс
                if (state.value === '' && keyboardMatch(event.nativeEvent, keyboardKeys.Backspace)) {
                    if (state.chips.length === 0) {
                        return;
                    }
                    const deletedChip = state.chips.pop();
                    onChange({
                        value: state.value,
                        chips: [...state.chips],
                        event: { type: 'remove', modifier: 'backspace', element: deletedChip },
                    });
                }
                // пытаемся найти ближайший чипс для фокуса
                if (state.value === '' && keyboardMatch(event.nativeEvent, keyboardKeys.ArrowLeft)) {
                    focusOnLastChip(event, chipsContainerRef.current, inputRef.current);
                }
            },
            [allowArbitraryValues, onChange, onChangeInputExternal, state]
        );

        // при выставлении высоты учитваем падинги
        // иначе высота будет больше значения свойства
        const scrollableAreaStyle = maxHeight
            ? { maxHeight: `${maxHeight - (size === 'medium' ? 16 : 24)}px` }
            : undefined;

        return (
            <div>
                <div
                    ref={chipsRootRefCallback}
                    className={classnames(styles.root, styles[size], {
                        [styles.focused]: isFocused,
                        [styles.focusVisible]: isFocusVisible,
                        [styles.disabled]: !!disabled,
                        [styles.invalid]: !!invalid,
                    })}
                    onFocus={() => {
                        if (disabled) {
                            return;
                        }
                        if (!inputRef.current) {
                            return;
                        }
                        setExpanded(true);
                        // добавляем .focus() еще и тут
                        // чтобы не сбрасывать фокус во время клика по chip
                        // или по клику на враппер
                        if (document.activeElement === chipsRootRef.current) {
                            inputRef.current.focus();
                        }
                    }}
                    onBlur={(event) => {
                        stateRef.current.hadFocusWhenClicked &&=
                            event.relatedTarget === chipsRootRef.current || event.relatedTarget === inputRef.current;
                        setExpanded(false);
                    }}
                    onKeyDown={onKeyDown}
                    onMouseDown={() => {
                        // при повторном клике по инпуту в фокусе нужно закрыть дроп
                        // при этом клик может прийтись на обертку, из-за чего инпут поймает blur и повторный фокус
                        // что усложняет проверку того, что инпут уже был в фокусе в момент клика
                        // поэтому в цепочке mouseDown -> blur -> focus -> click храним флаг, что фокус не вышел за пределы компонента
                        stateRef.current.hadFocusWhenClicked = document.activeElement === inputRef.current;
                        stateRef.current.wasExpandedWhenClicked = !!externalExpanded;
                    }}
                    onClick={() => {
                        if (
                            stateRef.current.hadFocusWhenClicked &&
                            stateRef.current.wasExpandedWhenClicked === externalExpanded
                        ) {
                            toggleExternalExpanded?.();
                        }
                        stateRef.current.hadFocusWhenClicked = false;
                        stateRef.current.wasExpandedWhenClicked = false;
                    }}
                    tabIndex={disabled ? -1 : 0}
                    data-qa={dataQa}
                >
                    <div style={scrollableAreaStyle} className={styles.scrollableArea}>
                        <div ref={chipsContainerRef} className={styles.chipsAndInput}>
                            {state.chips.map((chip, index) => {
                                return (
                                    <div
                                        key={`${chip.value}-${index}`}
                                        className={styles.customChipWrapper}
                                        onMouseDown={(event) => event.preventDefault()}
                                    >
                                        <CustomChip
                                            Element="button"
                                            size="small"
                                            disabled={disabled}
                                            onDelete={() => {
                                                deleteHandler(index);
                                            }}
                                            dataQaWrapper={`chips-trigger-chip-${chip.value}`}
                                            deleteOnly
                                        >
                                            {chip.label ?? chip.value}
                                        </CustomChip>
                                    </div>
                                );
                            })}
                            <div className={styles.field}>
                                <input
                                    ref={inputRefCallback}
                                    className={styles.fieldInput}
                                    type="text"
                                    disabled={disabled}
                                    value={state.value}
                                    onKeyDown={keyDownHandler}
                                    onFocus={focusHandler}
                                    onBlur={blurHandler}
                                    onChange={changeHandler}
                                    data-qa="chips-trigger-input"
                                    readOnly={isMobile}
                                />
                                {showLabel && (
                                    <div className={styles.labelWrapper}>
                                        <label className={styles.label}>{placeholder}</label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className={classnames(styles.icon, {
                            [styles.iconExpanded]: expanded || externalExpanded,
                        })}
                    >
                        <ChevronDownOutlinedSize24 />
                    </div>
                </div>
                <FormHelper
                    description={description}
                    errorMessage={errorMessage}
                    disabled={disabled}
                    invalid={invalid}
                />
            </div>
        );
    }
);

ChipsTrigger.displayName = 'ChipsTrigger';
